import { useState, useEffect } from 'react';
import { useI18n } from '../../i18n/useI18n';
import { useForm } from "react-hook-form";
import { useCreateCompany } from '../hooks/useCreateCompany';
import { useGetAllCompanies } from '../../users/hooks/useGetAllCompanies';
import { IFormData, ICompany } from '../../../api/companies/types';
import Drawer from '@/components/UI/Drawer/Drawer';
import Button from '@/components/UI/Button/Button';
import formStyles from '../../users/styles/userForm.module.scss';

interface CreateCompanyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  companyToEdit?: ICompany;
}

export const CreateCompanyDrawer = ({ isOpen, onClose, companyToEdit }: CreateCompanyDrawerProps) => {
  const { t } = useI18n();
  const { refetch } = useGetAllCompanies();
  const { isError, isPending, mutateAsync } = useCreateCompany();
  const [errorOnCreation, setErrorOnCreation] = useState("");
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      companyName: "",
    },
  });

  useEffect(() => {
    if (companyToEdit) {
      setValue('companyName', companyToEdit.nameCompany);
    } else {
      reset();
    }
  }, [companyToEdit, setValue, reset]);

  const handleClose = () => {
    reset();
    onClose();
    setErrorOnCreation("");
  };

  const onSubmit = async (formData: IFormData) => {
    try {
      await mutateAsync(
        { 
          ...formData,
          id: companyToEdit?.id
        }, 
        {
          onSuccess: () => {
            refetch();
            handleClose();
          },
          onError: (error) => {
            setErrorOnCreation(`${error?.message} -> ${watch("companyName")}`);
          }
        }
      );
    } catch (err: unknown) {
      console.error("Error al procesar la compañía:", err);
    }
  };

  const isEditing = Boolean(companyToEdit);
  const drawerTitle = isEditing 
    ? t("companies_module.modal_edit_title") 
    : t("companies_module.modal_title");
  const submitButtonText = isEditing
    ? isPending 
      ? t("companies_module.modal_button_updating")
      : t("companies_module.modal_button_update")
    : isPending
      ? t("companies_module.modal_button_creating")
      : t("companies_module.modal_button_create");

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Drawer.Header onClose={handleClose}>
        {drawerTitle}
      </Drawer.Header>
      <Drawer.Body>
        <form id="company-form" onSubmit={handleSubmit(onSubmit)} className={formStyles.formGrid}>
          <div className={formStyles.formField}>
            <label htmlFor='companyName' className={formStyles.label}>
              {t("companies_module.modal_input_name")}
            </label>
            <input
              id='companyName'
              className={formStyles.input}
              {...register('companyName', { required: true })}
              placeholder={t("companies_module.modal_input_name_placeholder")}
            />
            {errors.companyName && (
              <p className={formStyles.errorMessage}>
                {t("companies_module.modal_create_error_name")}
              </p>
            )}
            {isError && (
              <p className={formStyles.errorMessage}>
                {errorOnCreation}
              </p>
            )}
          </div>
        </form>
      </Drawer.Body>
      <Drawer.Footer>
        <div className={formStyles.formActions}>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            {t("companies_module.modal_button_cancel")}
          </Button>
          <Button type="submit" form="company-form" variant="primary" disabled={isPending}>
            {submitButtonText}
          </Button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}; 